import { Controller } from 'react-hook-form';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { BackButton } from '@/components/common/BackButton';
import { ErrorState } from '@/components/common/ErrorState';
import { PageLoader } from '@/components/common/PageLoader';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  companyListQueryOptions,
  departmentListQueryOptions,
  designationListQueryOptions,
} from '@/features/organization/api/organization.api';
import { useResetOnFieldChange } from '@/hooks/useResetOnFieldChange';
import { useEditUserForm } from '../hooks/useEditUserForm';
import {
  DATE_USER_FIELDS,
  NON_HIERARCHICAL_USER_TYPES,
  USER_STATUS,
} from '../constants/user.constants';
import type { UpdateUserPayload, UserStatus } from '../types/user.types';
import { DateField } from '../components/UserForm/DateField';
import { TextField } from '../components/UserForm/TextField';
import { SelectField } from '../components/UserForm/SelectField';
import { ReadOnlyField } from '../components/UserForm/ReadOnlyField';
import { USER_FORM_GRID_CLASS, type UserFormLookups } from '../components/UserForm/types';
import { formatDisplayDateTime } from '@/utils/date';
import { DROPDOWN_ITEM_CLASS, DROPDOWN_PANEL_CLASS } from '@/styles/floatingPanel';

const BASIC_FIELDS = ['fullName', 'username', 'hrEmpId', 'mobile', 'email'] as const;

const USER_STATUS_LABELS: Record<UserStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
};

const USER_STATUS_ITEMS = USER_STATUS.map((status) => ({
  value: status,
  label: USER_STATUS_LABELS[status],
}));

interface UserEditPageProps {
  userId: number;
  fallbackPath?: string | undefined;
}

export function UserEditPage({ userId, fallbackPath = '/users' }: UserEditPageProps) {
  const router = useRouter();
  const navigate = useNavigate();

  function goBack() {
    if (router.history.canGoBack()) {
      router.history.back();
    } else {
      void navigate({ to: fallbackPath });
    }
  }

  const {
    form,
    userData,
    isUserDataLoading,
    isUserDataFetching,
    userDataError,
    refetchUserData,
    isPending,
    submit,
    askBeforeClose,
    discardChangesDialog,
  } = useEditUserForm({ userId, onLeave: goBack });

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const selectedCompanyId = watch('companyId');

  useResetOnFieldChange({
    watch,
    setValue,
    whenChanged: 'companyId',
    reset: ['departmentId'],
  });

  const { data: companies, isLoading: companiesLoading } = useQuery(companyListQueryOptions());
  const { data: departments, isLoading: departmentsLoading } = useQuery(
    departmentListQueryOptions(
      selectedCompanyId !== undefined ? { companyId: selectedCompanyId } : {},
    ),
  );
  const { data: designations, isLoading: designationsLoading } = useQuery(
    designationListQueryOptions(),
  );

  const lookups: UserFormLookups = {
    companies,
    companiesLoading,
    departments,
    departmentsLoading,
    designations,
    designationsLoading,
  };

  const backButton = <BackButton onClick={() => askBeforeClose(goBack)} />;

  if (isUserDataLoading) return <PageLoader />;

  if (userDataError) {
    return (
      <ErrorState
        error={userDataError}
        fallbackTitle="Could not load this user"
        onRetry={() => void refetchUserData()}
        isRetrying={isUserDataFetching}
        action={backButton}
        fill
      />
    );
  }

  if (!userData) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <p className="text-sm font-medium text-muted-foreground">User not found</p>
        {backButton}
      </div>
    );
  }

  const showReportingManager = !NON_HIERARCHICAL_USER_TYPES.has(userData.userTypeId);

  return (
    <>
      <div className="flex flex-col gap-6">
        <PageHeader title="Edit User" leading={backButton} />

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-6">
              <section>
                <h3 className="text-sm font-semibold mb-4">Basic Information</h3>
                <div className={USER_FORM_GRID_CLASS}>
                  {BASIC_FIELDS.map((field) => (
                    <TextField<UpdateUserPayload>
                      key={field}
                      name={field}
                      register={register}
                      errors={errors}
                    />
                  ))}
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="status">Status</Label>
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Select<UserStatus>
                          items={USER_STATUS_ITEMS}
                          value={field.value ?? null}
                          onValueChange={(nextStatus) => {
                            if (nextStatus) field.onChange(nextStatus);
                          }}
                        >
                          <SelectTrigger id="status">
                            <SelectValue />
                            <SelectIcon />
                          </SelectTrigger>
                          <SelectContent className={DROPDOWN_PANEL_CLASS}>
                            {USER_STATUS_ITEMS.map((item) => (
                              <SelectItem
                                key={item.value}
                                value={item.value}
                                className={DROPDOWN_ITEM_CLASS}
                              >
                                {item.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.status?.message && (
                      <p className="text-xs text-destructive">{String(errors.status.message)}</p>
                    )}
                  </div>
                </div>
              </section>

              <section>
                <h3 className="text-sm font-semibold mb-4">Organization</h3>
                <div className={USER_FORM_GRID_CLASS}>
                  <SelectField<UpdateUserPayload>
                    name="companyId"
                    control={control}
                    errors={errors}
                    userTypeId={userData.userTypeId}
                    lookups={lookups}
                  />
                  <SelectField<UpdateUserPayload>
                    name="departmentId"
                    control={control}
                    errors={errors}
                    userTypeId={userData.userTypeId}
                    lookups={lookups}
                  />
                  <SelectField<UpdateUserPayload>
                    name="designationId"
                    control={control}
                    errors={errors}
                    userTypeId={userData.userTypeId}
                    lookups={lookups}
                  />
                  {showReportingManager && (
                    <SelectField<UpdateUserPayload>
                      name="parentUserId"
                      control={control}
                      errors={errors}
                      userTypeId={userData.userTypeId}
                      lookups={lookups}
                      initialParentUserName={userData.parentUserName ?? undefined}
                    />
                  )}
                  <SelectField<UpdateUserPayload>
                    name="geoUnitId"
                    control={control}
                    errors={errors}
                    userTypeId={userData.userTypeId}
                    lookups={lookups}
                    initialDivisionId={userData.divisionId ?? undefined}
                    initialDistrictId={userData.districtId ?? undefined}
                  />
                </div>
              </section>

              <section>
                <h3 className="text-sm font-semibold mb-4">Additional Information</h3>
                <div className={USER_FORM_GRID_CLASS}>
                  <TextField<UpdateUserPayload>
                    name="accountNumber"
                    register={register}
                    errors={errors}
                  />
                  <TextField<UpdateUserPayload>
                    name="nickname"
                    register={register}
                    errors={errors}
                  />
                  <TextField<UpdateUserPayload>
                    name="contactNo"
                    register={register}
                    errors={errors}
                  />
                  <TextField<UpdateUserPayload>
                    name="address"
                    register={register}
                    errors={errors}
                  />
                  {DATE_USER_FIELDS.map((field) => (
                    <DateField<UpdateUserPayload>
                      key={field}
                      name={field}
                      control={control}
                      errors={errors}
                    />
                  ))}
                  <ReadOnlyField
                    id="createdAt"
                    label="Created"
                    value={formatDisplayDateTime(userData.createdAt)}
                  />
                  <ReadOnlyField
                    id="updatedAt"
                    label="Last updated"
                    value={formatDisplayDateTime(userData.updatedAt)}
                  />
                </div>
              </section>

              <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" onClick={() => askBeforeClose(goBack)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isPending}>
                  {isPending ? (
                    <>
                      <Loader2 className="size-4 animate-spin" /> Updating...
                    </>
                  ) : (
                    'Update User'
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
      {discardChangesDialog}
    </>
  );
}
